import { Checkbox } from '@atlaskit/checkbox';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu';
import Textfield from '@atlaskit/textfield';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { messages } from '../../../../../messages/sign-up-form';
import type {
  AnalyticsTrackType,
  BitbucketWorkspace,
  WorkspaceType
} from '../../../../../model/wac-signup-form';
import {
  BitbucketAccountCheckbox,
  CompoundField,
  DropdownButtonStyle,
  DropdownButtonStyleOpen,
  DropdownTrigger,
  FieldContainer,
  Label,
  SitePickerTextfieldWrapper
} from './styled';

export type BitbucketAccountPickerProps = {
  bbWorkspaces: BitbucketWorkspace[];
  bbWorkspacesTotal: number;
  bbNameOverride: string;
  selectedSite: string | null;
  sitesCount: number;
  onAccountChange: (accountName: string) => void;
  bbAccountCopy: (isEnabled: boolean) => void;
  analyticsTrack: AnalyticsTrackType;
  isValidSiteName: boolean;
  isDisabled: boolean;
};

type OnOpenChangeProps = {
  readonly isOpen: boolean;
  readonly isDisabled: boolean;
  readonly event?: { [key: string]: any };
};

export function BitbucketAccountPicker({
  bbWorkspaces,
  bbAccountCopy,
  bbNameOverride,
  onAccountChange,
  selectedSite,
  sitesCount,
  isValidSiteName,
  isDisabled
}: BitbucketAccountPickerProps) {
  const [isCopyChecked, setIsCopyChecked] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [triggerText, setTriggerText] = useState({ ...messages.selectAccount });
  const [isCheckboxDisabled, setIsCheckboxDisabled] = useState(sitesCount > 1);
  const [bbAccountName, setBbAccountName] = useState('');

  useMemo(() => {
    if (selectedSite && selectedSite.length > 0) {
      setIsCheckboxDisabled(false);
    }
    if (!isValidSiteName) {
      setIsCheckboxDisabled(true);
    }
  }, [selectedSite, isValidSiteName]);

  useEffect(() => {
    if (!isValidSiteName) {
      setIsCopyChecked(false);
      bbAccountCopy(false);
    }
  }, [isValidSiteName]);

  useEffect(() => {
    if (bbWorkspaces?.[0]?.workspace?.name) {
      handleDropdownItemClick(bbWorkspaces[0].workspace);
    }
  }, []);

  const triggerButtonTheme = () => {
    const buttonStyles = !isDropdownOpen
      ? DropdownButtonStyle
      : Object.assign({}, DropdownButtonStyle, DropdownButtonStyleOpen);
    return {
      buttonStyles,
      spinnerStyles: {}
    };
  };

  const handleCopyAccount = () => {
    setIsCopyChecked(!isCopyChecked);

    bbAccountCopy(!isCopyChecked);
    if (isCopyChecked) {
      onAccountChange(bbAccountName);
    }
  };

  const handleDropdownItemClick = ({ name }: WorkspaceType) => {
    setTriggerText(
      Object.assign({}, messages.selectAccount, { defaultMessage: name })
    );
    onAccountChange(name);
    setBbAccountName(name);
  };

  const trigger = (
    <DropdownTrigger>
      <FormattedMessage {...triggerText} />
    </DropdownTrigger>
  );

  return (
    <FieldContainer
      className={'bitbucket-account-picker-field sign-up-field-container'}
    >
      <CompoundField>
        <Label>
          <FormattedMessage {...messages.yourBitbucketAccount} />
        </Label>
      </CompoundField>

      {isCopyChecked && (
        <SitePickerTextfieldWrapper>
          <Textfield
            className={'bitbucket-account-picker-field site-name input'}
            isRequired
            id={'site-field'}
            value={bbNameOverride}
            style={{ height: 'inherit', width: '60%', color: '#091E44' }}
            isDisabled={true}
          />
        </SitePickerTextfieldWrapper>
      )}

      {!isCopyChecked && bbWorkspaces.length === 1 && (
        <SitePickerTextfieldWrapper>
          <Textfield
            className={
              'bitbucket-account-picker-field bitbucket-workspace input'
            }
            isRequired
            id={'bb-workspace-field'}
            value={bbWorkspaces[0].workspace.name}
            style={{ height: 'inherit', width: '60%', color: '#091E44' }}
            isDisabled={true}
          />
        </SitePickerTextfieldWrapper>
      )}
      {!isCopyChecked && bbWorkspaces.length > 1 && (
        <DropdownMenu
          trigger={trigger}
          triggerType="button"
          shouldFitContainer={true}
          triggerButtonProps={{
            shouldFitContainer: true,
            theme: triggerButtonTheme,
            isDisabled
          }}
          onOpenChange={({ isOpen }: OnOpenChangeProps) =>
            setIsDropdownOpen(isOpen)
          }
        >
          {bbWorkspaces.map((workspace) => {
            return (
              <DropdownItem
                key={workspace.workspace.uuid}
                onClick={() => handleDropdownItemClick(workspace.workspace)}
              >
                {workspace.workspace.name}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      )}
      {
        <BitbucketAccountCheckbox>
          <Checkbox
            label={<FormattedMessage {...messages.bitbucketAccountDuplicate} />}
            isChecked={isCopyChecked}
            onChange={() => handleCopyAccount()}
            name="checkbox-basic"
            isDisabled={isCheckboxDisabled || isDisabled}
          />
        </BitbucketAccountCheckbox>
      }
    </FieldContainer>
  );
}
